import React, { FC, useEffect, useState } from 'react';
import { Button, Drawer, Form, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { WarningCard } from 'common/components';
import { normalizePhone, phoneFormatter, rules, showSuccessMessage } from 'common/helpers';
import { useUserContext } from 'common/context';
import { IFormValue } from 'common/models';
import { ArrowLeftLong } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IUpdateUserByAdminPayload } from 'entities/User/User.models';
import { selectIsUserLoading, selectUser, updateUserByAdmin } from 'entities/User/User.slice';

export const UserEditCard: FC = () => {
  const dispatch = useAppDispatch();
  // user
  const user = useAppSelector(selectUser);
  const isUserLoading = useAppSelector(selectIsUserLoading);

  const [formIsChanged, setFormIsChanged] = useState<boolean>(false);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [payload, setPayload] = useState<IUpdateUserByAdminPayload>({} as IUpdateUserByAdminPayload);
  const [form] = useForm();
  const { openEditUserCard, setOpenEditUserCard } = useUserContext();

  const onClose = () => {
    setFormIsChanged(false);
    setOpenWarningCard(false);
    setOpenEditUserCard(false);
  };

  const handleClose = () => {
    if (isUserLoading) {
      return;
    }

    if (formIsChanged) {
      setOpenEditUserCard(false);
      setOpenWarningCard(true);
      return;
    }

    onClose();
  };

  const onFormValuesChange = (value: IFormValue) => {
    setFormIsChanged(true);

    if ('phone' in value) {
      setPayload((prev) => ({ ...prev, phone: value.phone.length ? normalizePhone(value.phone) : null }));
    } else {
      setPayload((prev) => ({ ...prev, ...value }));
    }
  };

  const onSubmit = () => {
    dispatch(
      updateUserByAdmin({
        ...payload,
        onSuccess: () => {
          showSuccessMessage('Данные пользователя успешно изменены.');
          onClose();
        },
      }),
    );
  };

  useEffect(() => {
    if (openEditUserCard && user) {
      setPayload({ id: user.id });
      form.setFieldsValue({
        lastName: user.lastName,
        firstName: user.firstName,
        patronymic: user.patronymic,
        position: user.position,
        email: user.email,
        phone: phoneFormatter(user.phone),
      });
    }
  }, [openEditUserCard, user]);

  if (!user) {
    return null;
  }

  return (
    <>
      <Drawer
        rootClassName="redesign drawer user-edit-card"
        closeIcon={<ArrowLeftLong />}
        width={720}
        destroyOnClose
        open={openEditUserCard}
        onClose={handleClose}
        classNames={{ footer: 'justify-end' }}
        footer={[
          <Button key="cancel" className="button-lg secondary" loading={isUserLoading} onClick={handleClose}>
            Отмена
          </Button>,
          <Button key="save" className="button-lg primary" loading={isUserLoading} onClick={form.submit}>
            Сохранить
          </Button>,
        ]}
      >
        <div className="mb-14 ff-regular fs-xxl">Редактировать данные</div>

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          scrollToFirstError
          onValuesChange={onFormValuesChange}
          onFinish={onSubmit}
        >
          <div className="mb-14">
            <div className="mb-9 ff-regular fs-l">Персональные данные</div>

            <Form.Item label="Фамилия*" name="lastName" rules={[rules.required()]}>
              <Input placeholder="Введите фамилию" />
            </Form.Item>

            <Form.Item label="Имя*" name="firstName" rules={[rules.required()]}>
              <Input placeholder="Введите имя" />
            </Form.Item>

            <Form.Item label="Отчество" name="patronymic">
              <Input placeholder="Введите отчество" />
            </Form.Item>
          </div>

          <div className="mb-14">
            <div className="mb-9 ff-regular fs-l">Контактные данные</div>

            <Form.Item label="Должность" name="position">
              <Input placeholder="Введите должность" />
            </Form.Item>

            <Form.Item name="email" label="Электронная почта">
              <Input placeholder="Введите адрес электронной почты" disabled />
            </Form.Item>

            <Form.Item label="Контактный телефон" name="phone">
              <Input placeholder="Введите контактный телефон" disabled />
            </Form.Item>
          </div>
        </Form>
      </Drawer>

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что не хотите сохранить изменения?"
        subtitle="Введенные данные будут потеряны."
        confirmBtnTitle="Не сохранять"
        onConfirm={onClose}
        onClose={() => {
          setOpenWarningCard(false);
          setOpenEditUserCard(true);
        }}
      />
    </>
  );
};
